/**
 * SQLite on the Mac, and nowhere else.
 *
 * IndexedDB already keeps in-flight drafts; this is the library. Sermons,
 * notes, quotes and folders live here so a Saturday-night flight still opens
 * last week's manuscript. The website never loads this module.
 */

import Database from "@tauri-apps/plugin-sql";
import { APP_EVENTS } from "@/lib/brand";

let dbPromise: Promise<Database> | null = null;

/** The sync indicator watches this instead of polling the database. */
function announceOutboxChange() {
  window.dispatchEvent(new CustomEvent(APP_EVENTS.outboxChanged));
}

async function migrate(db: Database) {
  await db.execute(`
    CREATE TABLE IF NOT EXISTS kv (
      key TEXT PRIMARY KEY,
      value TEXT NOT NULL,
      updated_at INTEGER NOT NULL
    )
  `);
  await db.execute(`
    CREATE TABLE IF NOT EXISTS entities (
      kind TEXT NOT NULL,
      id INTEGER NOT NULL,
      payload TEXT NOT NULL,
      updated_at INTEGER NOT NULL,
      PRIMARY KEY (kind, id)
    )
  `);
  await db.execute(`
    CREATE TABLE IF NOT EXISTS responses (
      path TEXT PRIMARY KEY,
      payload TEXT NOT NULL,
      updated_at INTEGER NOT NULL
    )
  `);
  await db.execute(`
    CREATE TABLE IF NOT EXISTS outbox (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      method TEXT NOT NULL,
      endpoint TEXT NOT NULL,
      body TEXT,
      document_id INTEGER,
      expected_version INTEGER,
      created_at INTEGER NOT NULL,
      status TEXT NOT NULL DEFAULT 'pending'
    )
  `);
  // A creation queued offline shows up in lists under a temporary negative id;
  // this column remembers it so the drain can retire the placeholder once the
  // server hands back the real row.
  await db
    .execute("ALTER TABLE outbox ADD COLUMN local_id INTEGER")
    .catch(() => undefined);
  await db.execute(`
    CREATE TABLE IF NOT EXISTS snapshots (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      document_id INTEGER NOT NULL,
      title TEXT NOT NULL,
      content TEXT NOT NULL,
      version INTEGER,
      word_count INTEGER NOT NULL DEFAULT 0,
      created_at INTEGER NOT NULL
    )
  `);
  await db.execute(
    "CREATE INDEX IF NOT EXISTS snapshots_by_document ON snapshots (document_id, created_at)",
  );
}

export async function getDesktopDb(): Promise<Database> {
  if (!dbPromise) {
    dbPromise = Database.load("sqlite:sermonspace.db").then(async (db) => {
      await migrate(db);
      return db;
    });
  }
  return dbPromise;
}

export async function putEntity(kind: string, id: number, payload: unknown) {
  const db = await getDesktopDb();
  await db.execute(
    `INSERT INTO entities (kind, id, payload, updated_at)
     VALUES ($1, $2, $3, $4)
     ON CONFLICT(kind, id) DO UPDATE SET payload = excluded.payload, updated_at = excluded.updated_at`,
    [kind, id, JSON.stringify(payload), Date.now()],
  );
}

export async function getEntity<T>(kind: string, id: number): Promise<T | null> {
  const db = await getDesktopDb();
  const rows = await db.select<Array<{ payload: string }>>(
    "SELECT payload FROM entities WHERE kind = $1 AND id = $2",
    [kind, id],
  );
  if (!rows[0]) return null;
  try {
    return JSON.parse(rows[0].payload) as T;
  } catch {
    return null;
  }
}

export async function deleteEntity(kind: string, id: number) {
  const db = await getDesktopDb();
  await db.execute("DELETE FROM entities WHERE kind = $1 AND id = $2", [kind, id]);
}

export async function listEntities<T>(kind: string): Promise<T[]> {
  const db = await getDesktopDb();
  const rows = await db.select<Array<{ payload: string }>>(
    "SELECT payload FROM entities WHERE kind = $1",
    [kind],
  );
  const items: T[] = [];
  for (const row of rows) {
    try {
      items.push(JSON.parse(row.payload) as T);
    } catch {
      // A corrupt row must not empty the library.
    }
  }
  return items;
}

export async function putResponse(path: string, payload: unknown) {
  const db = await getDesktopDb();
  await db.execute(
    `INSERT INTO responses (path, payload, updated_at)
     VALUES ($1, $2, $3)
     ON CONFLICT(path) DO UPDATE SET payload = excluded.payload, updated_at = excluded.updated_at`,
    [path, JSON.stringify(payload), Date.now()],
  );
}

export async function getResponse<T>(path: string): Promise<T | null> {
  const db = await getDesktopDb();
  const rows = await db.select<Array<{ payload: string }>>(
    "SELECT payload FROM responses WHERE path = $1",
    [path],
  );
  if (!rows[0]) return null;
  try {
    return JSON.parse(rows[0].payload) as T;
  } catch {
    return null;
  }
}

export interface OutboxRow {
  id: number;
  method: string;
  endpoint: string;
  body: string | null;
  document_id: number | null;
  expected_version: number | null;
  created_at: number;
  status: "pending" | "conflict";
  local_id: number | null;
}

export async function listOutbox(status: OutboxRow["status"] = "pending"): Promise<OutboxRow[]> {
  const db = await getDesktopDb();
  return db.select<OutboxRow[]>(
    "SELECT id, method, endpoint, body, document_id, expected_version, created_at, status, local_id FROM outbox WHERE status = $1 ORDER BY id ASC",
    [status],
  );
}

export async function enqueueOutbox(row: {
  method: string;
  endpoint: string;
  body: unknown;
  documentId?: number | null;
  expectedVersion?: number | null;
  localId?: number | null;
}): Promise<void> {
  const db = await getDesktopDb();
  const body = row.body === undefined ? null : JSON.stringify(row.body);
  // One pending write per document: later keystrokes replace the payload and
  // keep the version we last successfully synced, which is what the server
  // will compare against. Two rows would replay an older body after a newer one.
  if (row.documentId != null) {
    const existing = await db.select<Array<{ id: number }>>(
      "SELECT id FROM outbox WHERE document_id = $1 AND status = 'pending'",
      [row.documentId],
    );
    if (existing[0]) {
      await db.execute("UPDATE outbox SET body = $1, endpoint = $2, method = $3 WHERE id = $4", [
        body,
        row.endpoint,
        row.method,
        existing[0].id,
      ]);
      announceOutboxChange();
      return;
    }
  }
  await db.execute(
    `INSERT INTO outbox (method, endpoint, body, document_id, expected_version, created_at, status, local_id)
     VALUES ($1, $2, $3, $4, $5, $6, 'pending', $7)`,
    [
      row.method,
      row.endpoint,
      body,
      row.documentId ?? null,
      row.expectedVersion ?? null,
      Date.now(),
      row.localId ?? null,
    ],
  );
  announceOutboxChange();
}

export async function deleteOutbox(id: number) {
  const db = await getDesktopDb();
  await db.execute("DELETE FROM outbox WHERE id = $1", [id]);
  announceOutboxChange();
}

export async function markOutboxConflict(id: number) {
  const db = await getDesktopDb();
  await db.execute("UPDATE outbox SET status = 'conflict' WHERE id = $1", [id]);
  announceOutboxChange();
}

export async function pendingOutboxCount(): Promise<number> {
  const db = await getDesktopDb();
  const rows = await db.select<Array<{ n: number }>>(
    "SELECT COUNT(*) as n FROM outbox WHERE status = 'pending'",
  );
  return rows[0]?.n ?? 0;
}

export async function conflictOutboxCount(): Promise<number> {
  const db = await getDesktopDb();
  const rows = await db.select<Array<{ n: number }>>(
    "SELECT COUNT(*) as n FROM outbox WHERE status = 'conflict'",
  );
  return rows[0]?.n ?? 0;
}

/* ------------------------------------------------------------------ *
 * Version history
 *
 * Local snapshots of a sermon as it stood at past saves. They answer
 * "what did this say on Thursday?" without a server round-trip, and they
 * work offline because the archive is on disk.
 * ------------------------------------------------------------------ */

export interface SnapshotRow {
  id: number;
  document_id: number;
  title: string;
  content: string;
  version: number | null;
  word_count: number;
  created_at: number;
}

export async function latestSnapshot(documentId: number): Promise<SnapshotRow | null> {
  const db = await getDesktopDb();
  const rows = await db.select<SnapshotRow[]>(
    "SELECT id, document_id, title, content, version, word_count, created_at FROM snapshots WHERE document_id = $1 ORDER BY created_at DESC, id DESC LIMIT 1",
    [documentId],
  );
  return rows[0] ?? null;
}

export async function insertSnapshot(row: {
  documentId: number;
  title: string;
  content: string;
  version: number | null;
  wordCount: number;
}): Promise<void> {
  const db = await getDesktopDb();
  await db.execute(
    `INSERT INTO snapshots (document_id, title, content, version, word_count, created_at)
     VALUES ($1, $2, $3, $4, $5, $6)`,
    [row.documentId, row.title, row.content, row.version, row.wordCount, Date.now()],
  );
}

export async function listSnapshots(
  documentId: number,
  limit = 200,
): Promise<SnapshotRow[]> {
  const db = await getDesktopDb();
  return db.select<SnapshotRow[]>(
    "SELECT id, document_id, title, content, version, word_count, created_at FROM snapshots WHERE document_id = $1 ORDER BY created_at DESC, id DESC LIMIT $2",
    [documentId, limit],
  );
}

export async function pruneSnapshots(documentId: number, keep = 200): Promise<void> {
  const db = await getDesktopDb();
  await db.execute(
    `DELETE FROM snapshots WHERE document_id = $1 AND id NOT IN (
       SELECT id FROM snapshots WHERE document_id = $1 ORDER BY created_at DESC, id DESC LIMIT $2
     )`,
    [documentId, keep],
  );
}
